import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { AppDispatch } from "../../app/store";
import { addItemToCart } from "../../services/cartSlice/cartSlice";
import { gaEvent, gaValue, toGaItem } from "../../analytics";
import { ItemType } from "../../types/types";

// ── URL helpers ──────────────────────────────────────────────────────
export const useQuery = () => new URLSearchParams(useLocation().search);

// ── Date navigation ──────────────────────────────────────────────────
export const navigateToDay = (date: string, offset: number): string => {
  const d = new Date(date);
  d.setDate(d.getDate() + offset);
  return d.toISOString().split("T")[0];
};

// ── Order helpers ────────────────────────────────────────────────────
export const generateOrderId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomString = Math.random().toString(36).slice(2);
  return `${timestamp}-${randomString}`;
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[+]?[0-9]{10,15}$/;
  return phoneRegex.test(phone);
};

// ── Input filters ────────────────────────────────────────────────────
export const onlyNumbers = (value: string): string => value.replace(/[^0-9]/g, "");

// ── Type coercion ────────────────────────────────────────────────────
export const toStringSafe = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

// ── Add to cart hook ─────────────────────────────────────────────────
export const useAddToCart = () => {
  const dispatch = useDispatch<AppDispatch>();

  // `quantity` defaults to 1 (EB-538) so the callers that have no selector — the cart drawer, the
  // admin preview — keep working untouched. `value` is the line total, not the unit price: GA4
  // sums it into revenue, so reporting the unit price on a three-unit add would under-report by
  // two thirds. Both the total and the item entry come from the shared builders (EB-558), so the
  // discounted price they report cannot drift from what `purchase` will record.
  const addToCart = useCallback(
    (payload: ItemType, quantity = 1) => {
      dispatch(addItemToCart({ ...payload, quantity }));

      gaEvent("add_to_cart", {
        currency: "COP",
        value: gaValue([{ ...payload, quantity }]),
        items: [toGaItem(payload, { quantity })],
      });
    },
    [dispatch],
  );

  return addToCart;
};
